import React, { useState, useEffect, useCallback } from 'react';
import { mastersAPI } from '../services/api';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, FolderTree, Users, Truck, Route, Receipt, User2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const MASTER_TYPES = [
  { value: 'group', label: 'Groups', icon: FolderTree, color: 'bg-slate-100 text-slate-700' },
  { value: 'sub_group', label: 'Sub-Groups', icon: FolderTree, color: 'bg-slate-50 text-slate-600' },
  { value: 'client', label: 'Clients', icon: Users, color: 'bg-emerald-50 text-emerald-700' },
  { value: 'vendor', label: 'Vendors', icon: Receipt, color: 'bg-violet-50 text-violet-700' },
  { value: 'route', label: 'Routes', icon: Route, color: 'bg-blue-50 text-blue-700' },
  { value: 'vehicle', label: 'Vehicles', icon: Truck, color: 'bg-orange-50 text-orange-700' },
  { value: 'driver', label: 'Drivers', icon: User2, color: 'bg-amber-50 text-amber-700' },
  { value: 'expense_category', label: 'Expense Categories', icon: Receipt, color: 'bg-rose-50 text-rose-700' },
];

export default function Masters() {
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('client');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ name: '', master_type: 'client', parent_id: '', details: {} });
  const [detailFields, setDetailFields] = useState({ address: '', phone: '', gstin: '', contact_person: '', vehicle_number: '', license_number: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { master_type: activeType };
      if (search) params.search = search;
      const res = await mastersAPI.list(params);
      setMasters(res.data);
    } catch { toast.error('Failed to load masters'); }
    finally { setLoading(false); }
  }, [activeType, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', master_type: activeType, parent_id: '', details: {} });
    setDetailFields({ address: '', phone: '', gstin: '', contact_person: '', vehicle_number: '', license_number: '' });
    setDialog({ open: true, editing: null });
  };

  const openEdit = (m) => {
    setForm({ name: m.name, master_type: m.master_type, parent_id: m.parent_id || '', details: m.details || {} });
    setDetailFields({ address: '', phone: '', gstin: '', contact_person: '', vehicle_number: '', license_number: '', ...m.details });
    setDialog({ open: true, editing: m.master_id });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const payload = { ...form, details: { ...detailFields } };
    try {
      if (dialog.editing) {
        await mastersAPI.update(dialog.editing, payload);
        toast.success('Master updated');
      } else {
        await mastersAPI.create(payload);
        toast.success('Master created');
      }
      setDialog({ open: false, editing: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this master?')) return;
    try {
      await mastersAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const activeConfig = MASTER_TYPES.find(t => t.value === activeType);
  const getDetailFields = () => {
    switch (activeType) {
      case 'client': case 'vendor': return ['address', 'phone', 'gstin', 'contact_person'];
      case 'vehicle': return ['vehicle_number', 'vehicle_type', 'capacity'];
      case 'driver': return ['phone', 'license_number', 'address'];
      case 'route': return ['from_city', 'to_city', 'distance_km'];
      default: return [];
    }
  };

  return (
    <div data-testid="masters-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Masters</h1>
          <p className="text-sm text-slate-500 mt-1">Manage groups, clients, vendors, routes, vehicles, drivers</p>
        </div>
        <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="master-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add {activeConfig?.label?.replace(/s$/, '')}
        </Button>
      </div>

      {/* Type Tabs */}
      <div className="flex flex-wrap gap-2 bg-white border border-slate-200 rounded-md p-3">
        {MASTER_TYPES.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.value} onClick={() => { setActiveType(t.value); setSearch(''); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-colors ${
                activeType === t.value ? 'bg-orange-600 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
              }`} data-testid={`master-tab-${t.value}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Search + Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder={`Search ${activeConfig?.label || ''}...`} value={search}
              onChange={(e) => setSearch(e.target.value)} className="form-input pl-9" data-testid="master-search" />
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <table className="w-full data-table" data-testid="master-table">
            <thead><tr><th>Name</th><th>Details</th><th>Created</th><th className="text-center w-24">Actions</th></tr></thead>
            <tbody>
              {masters.map(m => (
                <tr key={m.master_id} data-testid={`master-row-${m.master_id}`}>
                  <td className="font-medium text-sm text-slate-800">{m.name}</td>
                  <td className="text-xs text-slate-500">
                    {Object.entries(m.details || {}).filter(([, v]) => v).slice(0, 3).map(([k, v]) => (
                      <span key={k} className="mr-3">{k}: <strong>{v}</strong></span>
                    ))}
                  </td>
                  <td className="text-xs text-slate-400">{m.created_at?.split('T')[0]}</td>
                  <td>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEdit(m)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`master-edit-${m.master_id}`}>
                        <Edit className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => handleDelete(m.master_id)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`master-del-${m.master_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {masters.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-12">No {activeConfig?.label?.toLowerCase()} yet</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, editing: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{dialog.editing ? 'Edit' : 'Add'} {activeConfig?.label?.replace(/s$/, '')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="form-label">Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="form-input" placeholder="Enter name..." data-testid="master-form-name" />
            </div>
            {getDetailFields().map(field => (
              <div key={field}>
                <label className="form-label capitalize">{field.replace(/_/g, ' ')}</label>
                <input type="text" value={detailFields[field] || ''} onChange={(e) => setDetailFields(d => ({ ...d, [field]: e.target.value }))}
                  className="form-input" data-testid={`master-form-${field}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="master-form-save">
              {dialog.editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
